import { put, call, takeEvery } from 'redux-saga/effects';
import { isEqual } from 'lodash';
import { UserRole } from '@upstart/harmony/lib/constants';
import { replace, go } from 'connected-react-router';
import { MfaStatusType } from '@upstart/harmony/lib/legos/constants/user';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import {
  isEmployeeOrAdvancedUser,
  isEmployee,
} from '@upstart/harmony/lib/legos/userTypes';
import { clearUserProperties } from 'util/amplitude';
import {
  AUTH_REQUEST,
  ACCESS_DEALER_REQUEST,
  ACCESS_ADMIN_REQUEST,
  LOGOUT_REQUEST,
  REDIRECT_REQUEST,
  OTP_VERIFY_REQUEST,
  OTP_RESEND_REQUEST,
  EXPIRE_SESSION_REQUEST,
  OKTA_REDIRECT,
} from './actionTypes';
import {
  authSuccess,
  authFailure,
  accessDealerSuccess,
  accessDealerFailure,
  accessAdminSuccess,
  accessAdminFailure,
} from './actions';
import {
  currentUserDealersRequest,
  currentUserDealersSuccess,
  currentUserSuccess,
  logoutUser,
} from '../user/actions';
import { getCurrentUser } from '../user/sagas';
import { getDealerTask } from '../dealers/sagas';
import { currentUserPermissionsRequest } from '../permissions/actions';
import AuthService from '../../services/AuthService';
import UserService from '../../services/UserService';
import DealerService from '../../services/DealerService';
import buildMenu from '../../components/DashboardSidebar/menuBuilder';
import {
  deleteMfaLoginToken,
  setMfaLoginToken,
  setLoginCred,
  deleteLoginCred,
  setLegacyLoginDisabled,
} from '../logins/actions';

import {
  removeMfaSessionToken,
  setMfaSessionToken,
} from '../../util/tokenUtils';
import { PageMessage } from '../../components/GlobalUI';
import { logout } from './base-sagas';

const shouldRedirectToCommandCenter = (user, dealer) =>
  dealer?.wizard?.enableCommandCenter && !isEmployee(user);

// access chairman as a Dealer
export function* accessDealer({ payload }) {
  try {
    const dealer = payload;
    const dealerId = payload.id;
    yield call(AuthService.updateSession, dealerId);
    // We call AuthService again to populate the user with other data we need
    // such as feature flags and new dealer_id.
    const user = yield call(getCurrentUser);
    // Update the current session with this dealer id

    if (shouldRedirectToCommandCenter(user, dealer)) {
      yield call(AuthService.updateSession, dealerId);
      window.location.assign(
        `${process.env.REACT_APP_COMMAND_CENTER_URL}?welcome`,
      );
      return;
    }
    yield put(accessDealerSuccess());

    const menu = buildMenu(user);
    // Fetch Updated Dealer
    yield call(getDealerTask, { payload: { dealerId } });
    // go to the first accessible menu item for this user
    yield put(replace(menu[0].getUrl(user)));
  } catch (error) {
    yield put(accessDealerFailure(error));
    yield call(logout);
  }
}

// access chairman as Admin
export function* accessAdmin() {
  try {
    yield put(accessAdminSuccess());
    yield put(currentUserPermissionsRequest());
    // We call AuthService again to populate the user with other data we need
    // such as feature flags.
    yield call(getCurrentUser);
    yield put(replace('/dealers'));
  } catch (error) {
    yield put(accessAdminFailure(error));
    yield call(logout);
  }
}

// redirect a user after login based on their role and dealership access
function* redirect(user) {
  yield put(currentUserPermissionsRequest());
  // only employee or advanced users with more than just USER role are allowed to access Chairman
  if (isEmployeeOrAdvancedUser(user) && !isEqual(user.roles, [UserRole.USER])) {
    yield put(currentUserDealersRequest());
    const dealers = yield call(UserService.getDealers);
    yield put(currentUserDealersSuccess(dealers));
    // redirect non-admins with one or no dealerships appropriately
    // else admins and employees with multiple dealer access choose a dealer to access
    if (
      !(
        user.roles.includes(UserRole.SUPER_ADMIN) ||
        user.roles.includes(UserRole.ADMIN) ||
        user.roles.includes(UserRole.READ_ONLY_ADMIN)
      )
    ) {
      if (dealers.length === 1) {
        // default to the only dealer that's accessible
        yield call(accessDealer, { payload: dealers[0] });
      } else if (dealers.length === 0) {
        yield put(replace('/'));
      }
    }
  } else {
    yield put(replace('/'));
  }
}

export function* login({ payload }) {
  try {
    if (!payload.skipMfa) {
      const response =
        'code' in payload && 'state' in payload
          ? yield call(AuthService.oktaLogin, {
              code: payload.code,
              state: payload.state,
            })
          : yield call(AuthService.mfaLogin, payload);
      if (response.status === MfaStatusType.SUCCESS) {
        const { user } = response;
        const dealer = user.dealer_id
          ? yield DealerService.getDealer(user.dealer_id)
          : null;
        if (dealer && shouldRedirectToCommandCenter(user, dealer)) {
          window.location.assign(
            `${process.env.REACT_APP_COMMAND_CENTER_URL}?welcome`,
          );
          return;
        }

        yield call(AuthService.setToken, user.token);
        yield put(authSuccess());
        yield call(getCurrentUser);
        yield put(deleteLoginCred());
        yield call(redirect, user);
      } else if (response.status === MfaStatusType.MFA_REQUIRED) {
        yield put(authSuccess());
        yield call(removeMfaSessionToken);
        yield put(setMfaLoginToken(response.mfaToken));
        yield put(setLegacyLoginDisabled(response.legacyLoginDisabled));
        yield put(setLoginCred(payload));
      }
    } else {
      const user = yield call(AuthService.login, payload);
      yield call(AuthService.setToken, user.token);
      yield put(authSuccess());
      yield call(getCurrentUser);
      yield put(deleteLoginCred());
      yield call(redirect, user);
    }
  } catch (error) {
    yield put(authFailure(error));
    yield call(logout);
  }
}

export function* oktaRedirect({ payload }) {
  yield call(AuthService.oktaRedirect, payload);
}

export function* verifyOTP({ payload }) {
  try {
    if (!payload.rememberDevice) {
      yield call(removeMfaSessionToken);
    }

    const response = yield call(AuthService.multiFactor, payload);

    if (response.status === MfaStatusType.SUCCESS) {
      if (payload.rememberDevice) {
        setMfaSessionToken(response.mfaToken);
      }

      yield put(deleteMfaLoginToken());

      const { user } = response;
      yield call(AuthService.setToken, user.token);
      yield put(authSuccess());
      yield call(getCurrentUser);
      yield put(deleteLoginCred());
      yield call(redirect, user);
    }
  } catch (error) {
    yield put(authFailure(error));

    if (error.status === 401) {
      try {
        const errorObj = JSON.parse(error.error);
        if (
          errorObj.status === MfaStatusType.TOKEN_EXPIRED ||
          errorObj.status === MfaStatusType.TOKEN_INVALID
        ) {
          yield put(deleteMfaLoginToken());
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          'There was an unexpected error on verify OTP. Please try again and contact support if the problem persists.',
          e,
        );
      }
    }
  }
}

export function* resendOTP({ payload }) {
  try {
    yield call(AuthService.resendOtp, payload);
    PageMessage({
      message: 'We sent you a code. It may take a few minutes to arrive.',
      type: MessageType.SUCCESS,
    });
  } catch (error) {
    yield put(authFailure(error));

    if (error.status === 401) {
      try {
        const errorObj = JSON.parse(error.error);
        if (
          errorObj.status === MfaStatusType.TOKEN_EXPIRED ||
          errorObj.status === MfaStatusType.TOKEN_INVALID
        ) {
          yield put(deleteMfaLoginToken());
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          'There was an unexpected error on resend OTP. Please try again and contact support if the problem persists.',
          e,
        );
      }
    }
  }
}

export function* redirectIfLoggedIn() {
  try {
    if (AuthService.isLoggedIn()) {
      const user = yield call(AuthService.getCurrentUser);
      yield put(currentUserSuccess(user));
      yield call(redirect, user);
    }
  } catch (error) {
    yield put(authFailure(error));

    yield put(replace('/login'));
  }
}

export function* expireSessionRequest() {
  clearUserProperties();
  yield put(replace('/expiredSession'));
  // go() hard refreshes the page, and makes any dangling modal disappear (so we don't need to track all modals and manually close all of them - imagine the work!)
  yield put(go(0));
  yield put(logoutUser());
}

export function* authSaga() {
  yield takeEvery(AUTH_REQUEST, login);
  yield takeEvery(OKTA_REDIRECT, oktaRedirect);
  yield takeEvery(REDIRECT_REQUEST, redirectIfLoggedIn);
  yield takeEvery(ACCESS_DEALER_REQUEST, accessDealer);
  yield takeEvery(ACCESS_ADMIN_REQUEST, accessAdmin);
  yield takeEvery(LOGOUT_REQUEST, logout);
  yield takeEvery(OTP_VERIFY_REQUEST, verifyOTP);
  yield takeEvery(OTP_RESEND_REQUEST, resendOTP);
  yield takeEvery(EXPIRE_SESSION_REQUEST, expireSessionRequest);
}
